import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { RoleProvider } from "../../contexts/RoleContext";
import {
  getProfileCompletionPath,
  isProfileCompletionPath,
} from "../../utils/onboarding";

interface RequireAuthProps {
  children: React.ReactNode;
}

const RequireAuth: React.FC<RequireAuthProps> = ({ children }) => {
  const {
    isAuthenticated,
    isLoading,
    user,
    checkAuth,
    isNewUser,
    clearNewUserFlag,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);
  const onProfileCompletionPath = isProfileCompletionPath(location.pathname);

  useEffect(() => {
    const verifyAuth = async () => {
      if (!isAuthenticated) {
        await checkAuth();
      }
      setIsChecking(false);
    };

    verifyAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Свежезарегистрированного пользователя один раз направляем на заполнение
  // профиля. Дальше требования закрывает блокирующий RequirementsGate, поэтому
  // флаг сбрасывается сразу по прибытии: покинуть страницу можно свободно,
  // а повторного редиректа (и петли) не будет.
  useEffect(() => {
    if (isNewUser && onProfileCompletionPath) {
      clearNewUserFlag();
    }
  }, [isNewUser, onProfileCompletionPath, clearNewUserFlag]);

  useEffect(() => {
    if (!isLoading && !isChecking && isAuthenticated && user) {
      // Redirect admin users to admin dashboard
      if (user.role === "admin" || user.role === "super_admin") {
        if (location.pathname === "/home" || location.pathname === "/") {
          navigate("/admin/dashboard", { replace: true });
        }
      }
      // Redirect dispatcher users to FSM board
      if (user.role === "dispatcher") {
        if (location.pathname === "/home" || location.pathname === "/") {
          navigate("/fsm/board", { replace: true });
        }
      }
    }
  }, [isLoading, isChecking, isAuthenticated, user, navigate, location]);

  useEffect(() => {
    if (!isLoading && !isChecking && !isAuthenticated) {
      navigate("/login", { replace: true, state: { from: location } });
    }
  }, [isLoading, isChecking, isAuthenticated, navigate, location]);

  // Одноразовый редирект нового пользователя на заполнение профиля. Работает
  // вместе с блокирующим RequirementsGate: тот удерживает на странице, пока
  // сервер возвращает `name`/`phone` в `missing`, а этот флаг даёт лишь
  // первоначальный толчок и сбрасывается по прибытии.
  useEffect(() => {
    if (!isLoading && !isChecking && isAuthenticated && isNewUser && !onProfileCompletionPath) {
      navigate(getProfileCompletionPath(), { replace: true });
    }
  }, [
    isLoading,
    isChecking,
    isAuthenticated,
    isNewUser,
    onProfileCompletionPath,
    navigate,
  ]);

  if (isLoading || isChecking) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div>
          <div style={{ fontSize: "24px", marginBottom: "16px" }}>⏳</div>
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (!user) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div>
          <div style={{ fontSize: "24px", marginBottom: "16px" }}>⚠️</div>
          <div>Loading user data...</div>
        </div>
      </div>
    );
  }

  return <RoleProvider>{children}</RoleProvider>;
};

export default RequireAuth;
